/* eslint-disable no-unused-vars */
/**
 * React hook for managing story idea generation state
 *
 * Uses the queue-centric architecture:
 * 1. Call gen-story-ideas → get jobId
 * 2. Use useQueueJob to poll for completion
 * 3. When job completes, extract ideas from output
 *
 * @fileoverview Custom hook for story idea generation workflow
 */

import { useState, useCallback, useEffect, useRef } from 'react';
import { logger } from '@musedrops/shared';
import { useQueueJob } from './useQueueJob';
import { getEdgeFunctionAuthHeaders } from '../services/supabase';

interface StoryIdea {
  topic: string;
  brief: string;
  slug: string;
  takeaway?: string;
  taxonomy?: string;
  imagePrompt?: string;
}

interface GenerateIdeasParams {
  showSlug: string;
  storyIdeaCount: number;
  shouldSave?: boolean;
}

interface QueueJobResponse {
  jobId: number;
  showSlug: string;
  message: string;
}

// Hook state for managing story idea generation
interface UseStoryIdeasState {
  loading: boolean;
  ideas: StoryIdea[];
  error: string | null;
  jobId: number | null;
  generateIdeas: (params: GenerateIdeasParams) => Promise<void>;
  clearError: () => void;
  clearIdeas: () => void;
}

/**
 * Call the gen-story-ideas edge function to create a queue job.
 * Returns job ID immediately - use useQueueJob hook to poll for completion.
 */
export async function createStoryIdeasJob(
  params: GenerateIdeasParams
): Promise<{ success: boolean; result?: QueueJobResponse; error?: string }> {
  const startTime = performance.now();
  logger.info('⏱️ [TIMING] Starting API call to gen-story-ideas', { params });

  try {
    const fetchStart = performance.now();
    const response = await fetch(
      `${import.meta.env.VITE_SUPABASE_URL}/functions/v1/gen-story-ideas`,
      {
        method: 'POST',
        headers: await getEdgeFunctionAuthHeaders(),
        body: JSON.stringify({
          showSlug: params.showSlug,
          storyIdeaCount: params.storyIdeaCount,
          shouldSave: params.shouldSave ?? false,
          trigger: 'manual', // UI-initiated jobs get priority
        }),
      }
    );
    const fetchEnd = performance.now();
    logger.info('⏱️ [TIMING] Fetch completed', {
      fetchDuration: `${(fetchEnd - fetchStart).toFixed(0)}ms`,
      status: response.status,
    });

    const parseStart = performance.now();
    const data = await response.json();
    const parseEnd = performance.now();
    logger.info('⏱️ [TIMING] JSON parsed', {
      parseDuration: `${(parseEnd - parseStart).toFixed(0)}ms`,
      totalDuration: `${(parseEnd - startTime).toFixed(0)}ms`,
      data,
    });

    if (!response.ok) {
      return {
        success: false,
        error: data.error || 'API Error',
      };
    }

    if (!data.success || !data.result?.jobId) {
      return {
        success: false,
        error: data.error || 'Failed to create queue job',
      };
    }

    return {
      success: true,
      result: data.result,
    };
  } catch (error) {
    logger.error('Create story ideas job failed', { error });
    return {
      success: false,
      error: error instanceof Error ? error.message : 'Network error',
    };
  }
}

/**
 * Custom hook for managing story idea generation.
 *
 * Uses queue-centric architecture with real-time polling:
 * - Creates queue job via edge function
 * - Polls for job completion using Supabase real-time
 * - Extracts ideas from job output when complete
 *
 * @returns {UseStoryIdeasState} State object with ideas, loading status, and control functions
 *
 * @example
 * ```tsx
 * const { ideas, loading, generateIdeas, error } = useStoryIdeas();
 *
 * const handleGenerate = async () => {
 *   await generateIdeas({ showSlug: 'my-show', storyIdeaCount: 5 });
 *   // ideas will be populated when job completes
 * };
 * ```
 */
export const useStoryIdeas = (): UseStoryIdeasState => {
  const [loading, setLoading] = useState(false);
  const [ideas, setIdeas] = useState<StoryIdea[]>([]);
  const [error, setError] = useState<string | null>(null);
  const [jobId, setJobId] = useState<number | null>(null);

  // Track if we're expecting a job completion
  const expectingJobRef = useRef(false);

  const clearError = useCallback(() => {
    setError(null);
  }, []);

  const clearIdeas = useCallback(() => {
    setIdeas([]);
  }, []);

  // Handle job completion
  const handleJobComplete = useCallback((output: Record<string, unknown>) => {
    logger.info('Story ideas job completed', { output });

    // Extract stories array from output
    const stories = output?.stories as StoryIdea[] | undefined;

    if (stories && Array.isArray(stories) && stories.length > 0) {
      setIdeas(stories);
    } else {
      setIdeas([]);
      setError('No ideas were generated. Please try again.');
    }

    setLoading(false);
    expectingJobRef.current = false;
  }, []);

  // Handle job error
  const handleJobError = useCallback((errorMsg: string) => {
    logger.error('Story ideas job failed', { error: errorMsg });
    setError(errorMsg);
    setIdeas([]);
    setLoading(false);
    expectingJobRef.current = false;
  }, []);

  // Handle job timeout
  const handleJobTimeout = useCallback(() => {
    setError('Story ideas generation timed out. Please try again.');
    setLoading(false);
    expectingJobRef.current = false;
  }, []);

  // Use queue job hook for polling
  const { watchJob, status, stopWatching } = useQueueJob({
    timeout: 3 * 60 * 1000, // 3 minutes for story ideas
    onComplete: handleJobComplete,
    onError: handleJobError,
    onTimeout: handleJobTimeout,
  });

  // Update loading state based on job status
  useEffect(() => {
    if (status && expectingJobRef.current) {
      if (status.status === 'processing') {
        setLoading(true);
      }
    }
  }, [status]);

  const generateIdeas = useCallback(
    async (params: GenerateIdeasParams) => {
      if (loading) {
        return; // Prevent multiple simultaneous requests
      }

      if (!params.showSlug?.trim()) {
        setError('Please select a show before generating ideas.');
        return;
      }

      setLoading(true);
      setError(null);
      setIdeas([]);
      expectingJobRef.current = true;

      try {
        // Create queue job
        const response = await createStoryIdeasJob(params);

        if (response.success && response.result?.jobId) {
          const newJobId = response.result.jobId;
          setJobId(newJobId);

          logger.info('Story ideas job created, watching for completion', {
            jobId: newJobId,
          });

          // Start watching the job for completion
          watchJob(newJobId);
        } else {
          const errorMessage =
            response.error || 'Failed to create generation job';
          setError(errorMessage);
          setLoading(false);
          expectingJobRef.current = false;
        }
      } catch (err) {
        logger.error('Generate story ideas hook error', { error: err });
        const errorMessage =
          err instanceof Error ? err.message : 'An unexpected error occurred';
        setError(`Error generating ideas: ${errorMessage}`);
        setLoading(false);
        expectingJobRef.current = false;
      }
    },
    [loading, watchJob]
  );

  // Cleanup on unmount
  useEffect(() => {
    return () => {
      stopWatching();
    };
  }, [stopWatching]);

  return {
    loading,
    ideas,
    error,
    jobId,
    generateIdeas,
    clearError,
    clearIdeas,
  };
};
